import type { RenderContext, VideoFrame } from '../types';
import { WebGLShaderProgram } from '../gpu/shader';
import { BaseEffect } from './base';
import { VERTEX_FULLSCREEN, FRAG_HEAT_DISTORTION } from '../shaders/glsl';

const DEFAULTS = { strength: 0.6, speed: 0.4 };

let heatProg: WebGLShaderProgram | null = null;

/** Animated noise displacement (Phase 6, Step 17). */
export class HeatDistortionEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../types').EffectTimelineClip>) {
    super(id, 'heat_distortion', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const gl = context.gpu!.gl;
    if (!heatProg) heatProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_HEAT_DISTORTION);
    return this.runShaderPass(frame, heatProg, (_gl, prog) => {
      prog.setUniform1i('u_texture', 0);
      prog.setUniform1f('u_strength', params.strength);
      prog.setUniform1f('u_speed', params.speed);
      prog.setUniform1f('u_time', context.currentTime);
    }, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const canvas = document.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return frame;
    const w = frame.width;
    const h = frame.height;
    const srcCtx = (frame.canvas as HTMLCanvasElement).getContext('2d');
    if (!srcCtx) {
      ctx.drawImage(frame.canvas as CanvasImageSource, 0, 0);
      return { width: w, height: h, canvas, texture: null };
    }
    const src = srcCtx.getImageData(0, 0, w, h);
    const out = ctx.createImageData(w, h);
    const t = context.currentTime * params.speed;
    const hash = (x: number, y: number) => {
      const v = Math.sin(x * 127.1 + y * 311.7 + t) * 43758.5453;
      return v - Math.floor(v);
    };
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const n = hash(x * 0.06, y * 0.06);
        const sx = Math.min(w - 1, Math.max(0, x + Math.round((hash(x, y + t) - 0.5) * params.strength * 2)));
        const sy = Math.min(h - 1, Math.max(0, y + Math.round((n - 0.5) * params.strength * 6)));
        const si = (sy * w + sx) * 4;
        const oi = (y * w + x) * 4;
        out.data[oi] = src.data[si];
        out.data[oi + 1] = src.data[si + 1];
        out.data[oi + 2] = src.data[si + 2];
        out.data[oi + 3] = 255;
      }
    }
    ctx.putImageData(out, 0, 0);
    return { width: w, height: h, canvas, texture: null };
  }
}
